/**
 * examSession — question draw for the exam simulation (audit F-06).
 *
 * The official class-B exam draws 30 questions as 20 Grundstoff + 10
 * Zusatzstoff. Our question data carries NO Grundstoff/Zusatzstoff flag,
 * so that split cannot be reproduced faithfully — claiming otherwise
 * would be a fake. What we CAN control, and what the audit found broken,
 * is that a plain random draw produces wildly uneven exams:
 *
 * - Error-point mix: drawing 30 at random can yield far more (or far
 *   fewer) 5-point questions than the real exam, so the same learner
 *   passes one simulation and fails the next by luck. We therefore draw
 *   proportionally to the pool's own point distribution, which keeps the
 *   reachable error points comparable across runs.
 * - Topic spread: a random draw can pile 12 of 30 questions into one
 *   chapter. We cap per-chapter share so every simulation stays broad.
 *
 * Both are best-effort: if the pool is too small or too skewed (free
 * users draw from ~133 questions), the draw degrades gracefully to
 * "as close as possible" and finally to random top-up, never returning
 * fewer questions than available.
 */
import type { Question } from '../../shared/types';

/** Fisher-Yates — local copy so this module stays dependency-free. */
function shuffle<T>(input: T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Largest-remainder apportionment: distributes `count` slots across the
 * point buckets proportionally to their share of the pool, so the
 * rounded targets always sum to exactly `count`.
 */
export function targetPointDistribution(pool: Question[], count: number): Map<number, number> {
  const poolByPoints = new Map<number, number>();
  for (const q of pool) {
    poolByPoints.set(q.points, (poolByPoints.get(q.points) || 0) + 1);
  }
  const total = pool.length;
  const targets = new Map<number, number>();
  if (total === 0 || count <= 0) return targets;

  const remainders: { points: number; remainder: number }[] = [];
  let assigned = 0;
  for (const [points, poolCount] of poolByPoints) {
    const exact = (poolCount / total) * count;
    const floor = Math.floor(exact);
    targets.set(points, floor);
    assigned += floor;
    remainders.push({ points, remainder: exact - floor });
  }
  // Hand out the leftover slots to the largest remainders.
  remainders.sort((a, b) => b.remainder - a.remainder);
  let i = 0;
  while (assigned < count && remainders.length > 0) {
    const bucket = remainders[i % remainders.length];
    targets.set(bucket.points, (targets.get(bucket.points) || 0) + 1);
    assigned++;
    i++;
  }
  return targets;
}

/**
 * Builds one exam draw: `count` questions, point mix proportional to the
 * pool, chapters spread as evenly as the pool allows. Option order is
 * NOT shuffled here — callers do that (same as the previous behaviour).
 */
export function buildExamQuestions(pool: Question[], count: number): Question[] {
  if (pool.length <= count) return shuffle(pool);

  const targets = targetPointDistribution(pool, count);
  // Cap per chapter so no single topic dominates. With 30 questions over
  // 14 chapters that is 3; the cap is lifted in a second pass if the
  // pool cannot fill the draw otherwise (e.g. free users).
  const chapterCount = new Set(pool.map((q) => q.chapterId)).size || 1;
  const perChapterCap = Math.max(2, Math.ceil((count / chapterCount) * 1.5));

  const byPoints = new Map<number, Question[]>();
  for (const q of shuffle(pool)) {
    const bucket = byPoints.get(q.points);
    if (bucket) bucket.push(q);
    else byPoints.set(q.points, [q]);
  }

  const selected: Question[] = [];
  const usedIds = new Set<string>();
  const perChapter = new Map<number, number>();

  const take = (q: Question, respectCap: boolean): boolean => {
    if (usedIds.has(q.id)) return false;
    const used = perChapter.get(q.chapterId) || 0;
    if (respectCap && used >= perChapterCap) return false;
    selected.push(q);
    usedIds.add(q.id);
    perChapter.set(q.chapterId, used + 1);
    return true;
  };

  // Pass 1: fill each point bucket to its target, honouring the cap.
  for (const [points, target] of targets) {
    const candidates = byPoints.get(points) || [];
    let taken = 0;
    for (const q of candidates) {
      if (taken >= target) break;
      if (take(q, true)) taken++;
    }
  }

  // Pass 2: buckets that fell short (cap or pool) — retry without cap,
  // still preferring the correct point value.
  if (selected.length < count) {
    for (const [points, target] of targets) {
      const have = selected.filter((q) => q.points === points).length;
      if (have >= target) continue;
      for (const q of byPoints.get(points) || []) {
        if (selected.filter((s) => s.points === points).length >= target) break;
        take(q, false);
      }
    }
  }

  // Pass 3: still short (skewed pool) — top up with anything left.
  if (selected.length < count) {
    for (const q of shuffle(pool)) {
      if (selected.length >= count) break;
      take(q, false);
    }
  }

  return shuffle(selected.slice(0, count));
}
